// api/index.js - Central API Export Point
// The "barrel" that exports everything

import { apiClient } from './client';
import { userAPI } from './users';
import { campaignAPI } from './campaigns';
import { squadAPI } from './squad';
import { facilityAPI } from './facilities';
import { researchAPI } from './research';
import { missionAPI } from './missions';
import { eventAPI } from './events';

// ========== COMBINED API OBJECT ==========
// Maintains backward compatibility with existing code
// All methods are bound to their respective API instances

export const api = {
  // ===== Core Client =====
  request: apiClient.request.bind(apiClient),
  
  // ===== User API =====
  getUsers: userAPI.getUsers.bind(userAPI),
  getUser: userAPI.getUser.bind(userAPI),
  createUser: userAPI.createUser.bind(userAPI),
  updateUser: userAPI.updateUser.bind(userAPI),
  deleteUser: userAPI.deleteUser.bind(userAPI),
  updateUsername: userAPI.updateUsername.bind(userAPI),
  updateEmail: userAPI.updateEmail.bind(userAPI),
  updatePassword: userAPI.updatePassword.bind(userAPI),
  updatePreferences: userAPI.updatePreferences.bind(userAPI),
  getUserPreferences: userAPI.getUserPreferences.bind(userAPI),
  getUserStats: userAPI.getUserStats.bind(userAPI),
  getUserByEmail: userAPI.getUserByEmail.bind(userAPI),
  getUserByUsername: userAPI.getUserByUsername.bind(userAPI),
  
  // ===== Campaign API =====
  getCampaigns: campaignAPI.getCampaigns.bind(campaignAPI),
  getCampaign: campaignAPI.getCampaign.bind(campaignAPI),
  createCampaign: campaignAPI.createCampaign.bind(campaignAPI),
  updateCampaign: campaignAPI.updateCampaign.bind(campaignAPI),
  deleteCampaign: campaignAPI.deleteCampaign.bind(campaignAPI),
  saveCampaignProgress: campaignAPI.saveCampaignProgress.bind(campaignAPI),
  updateCampaignDay: campaignAPI.updateCampaignDay.bind(campaignAPI),
  getUserCampaigns: campaignAPI.getUserCampaigns.bind(campaignAPI),
  getActiveCampaign: campaignAPI.getActiveCampaign.bind(campaignAPI),
  
  // ===== Squad API =====
  getSquad: squadAPI.getSquad.bind(squadAPI),
  getSquadMember: squadAPI.getSquadMember.bind(squadAPI),
  createSquadMember: squadAPI.createSquadMember.bind(squadAPI),
  updateSquadMember: squadAPI.updateSquadMember.bind(squadAPI),
  deleteSquadMember: squadAPI.deleteSquadMember.bind(squadAPI),
  healSquadMember: squadAPI.healSquadMember.bind(squadAPI),
  promoteMember: squadAPI.promoteMember.bind(squadAPI),
  updateNickname: squadAPI.updateNickname.bind(squadAPI),
  specializeSoldier: squadAPI.specializeSoldier.bind(squadAPI),
  unlockSkill: squadAPI.unlockSkill.bind(squadAPI),
  updateLoadout: squadAPI.updateLoadout.bind(squadAPI),
  addExperience: squadAPI.addExperience.bind(squadAPI),
  batchUpdateSquad: squadAPI.batchUpdateSquad.bind(squadAPI),
  
  // ===== Facility API =====
  getFacilities: facilityAPI.getFacilities.bind(facilityAPI),
  getFacility: facilityAPI.getFacility.bind(facilityAPI),
  createFacility: facilityAPI.createFacility.bind(facilityAPI),
  updateFacility: facilityAPI.updateFacility.bind(facilityAPI),
  deleteFacility: facilityAPI.deleteFacility.bind(facilityAPI),
  upgradeFacility: facilityAPI.upgradeFacility.bind(facilityAPI),
  toggleFacility: facilityAPI.toggleFacility.bind(facilityAPI),
  batchUpdateFacilities: facilityAPI.batchUpdateFacilities.bind(facilityAPI),
  getActiveFacilities: facilityAPI.getActiveFacilities.bind(facilityAPI),
  getFacilitiesByType: facilityAPI.getFacilitiesByType.bind(facilityAPI),
  getFacilityAtPosition: facilityAPI.getFacilityAtPosition.bind(facilityAPI),
  
  // ===== Research API =====
  getResearch: researchAPI.getResearch.bind(researchAPI),
  getResearchItem: researchAPI.getResearchItem.bind(researchAPI),
  createResearch: researchAPI.createResearch.bind(researchAPI),
  updateResearch: researchAPI.updateResearch.bind(researchAPI),
  deleteResearch: researchAPI.deleteResearch.bind(researchAPI),
  updateResearchProgress: researchAPI.updateResearchProgress.bind(researchAPI),
  addResearchProgress: researchAPI.addResearchProgress.bind(researchAPI),
  completeResearch: researchAPI.completeResearch.bind(researchAPI),
  getActiveResearch: researchAPI.getActiveResearch.bind(researchAPI),
  getCompletedResearch: researchAPI.getCompletedResearch.bind(researchAPI),
  getResearchByType: researchAPI.getResearchByType.bind(researchAPI),
  getCurrentResearch: researchAPI.getCurrentResearch.bind(researchAPI),
  
  // ===== Mission API =====
  getMissions: missionAPI.getMissions.bind(missionAPI),
  getMission: missionAPI.getMission.bind(missionAPI),
  createMission: missionAPI.createMission.bind(missionAPI),
  updateMission: missionAPI.updateMission.bind(missionAPI),
  deleteMission: missionAPI.deleteMission.bind(missionAPI),
  startMission: missionAPI.startMission.bind(missionAPI),
  completeMission: missionAPI.completeMission.bind(missionAPI),
  failMission: missionAPI.failMission.bind(missionAPI),
  abortMission: missionAPI.abortMission.bind(missionAPI),
  getActiveMissions: missionAPI.getActiveMissions.bind(missionAPI),
  getInProgressMissions: missionAPI.getInProgressMissions.bind(missionAPI),
  getCompletedMissions: missionAPI.getCompletedMissions.bind(missionAPI),
  getMissionsByType: missionAPI.getMissionsByType.bind(missionAPI),
  getMissionsByDifficulty: missionAPI.getMissionsByDifficulty.bind(missionAPI),
  getMissionStats: missionAPI.getMissionStats.bind(missionAPI),
  
  // ===== Event API =====
  getCampaignEvents: eventAPI.getCampaignEvents.bind(eventAPI),
  getEvent: eventAPI.getEvent.bind(eventAPI),
  createEvent: eventAPI.createEvent.bind(eventAPI),
  updateEvent: eventAPI.updateEvent.bind(eventAPI),
  deleteEvent: eventAPI.deleteEvent.bind(eventAPI),
  logStoryEvent: eventAPI.logStoryEvent.bind(eventAPI),
  logCombatEvent: eventAPI.logCombatEvent.bind(eventAPI),
  logResourceEvent: eventAPI.logResourceEvent.bind(eventAPI),
  logSquadEvent: eventAPI.logSquadEvent.bind(eventAPI),
  getEventsByDay: eventAPI.getEventsByDay.bind(eventAPI),
  getEventsByType: eventAPI.getEventsByType.bind(eventAPI),
  getRecentEvents: eventAPI.getRecentEvents.bind(eventAPI),
  getEventsBetweenDays: eventAPI.getEventsBetweenDays.bind(eventAPI),
  getEventTimeline: eventAPI.getEventTimeline.bind(eventAPI),
  getEventSummary: eventAPI.getEventSummary.bind(eventAPI),
};

// ========== INDIVIDUAL MODULE EXPORTS ==========
// Import these directly for better tree-shaking and clearer code

export { apiClient } from './client';
export { userAPI } from './users';
export { campaignAPI } from './campaigns';
export { squadAPI } from './squad';
export { facilityAPI } from './facilities';
export { researchAPI } from './research';
export { missionAPI } from './missions';
export { eventAPI } from './events';

// ========== DEFAULT EXPORT ==========
export default api;

// ========== USAGE GUIDE ==========

/*
╔═══════════════════════════════════════════════════════════════╗
║                    HOW TO USE THIS API                        ║
╚═══════════════════════════════════════════════════════════════╝

OPTION 1: Combined API (Backward Compatible)
────────────────────────────────────────────
import { api } from './api';

await api.createSquadMember(...);
await api.getCampaign(...);
await api.createFacility(...);

✓ Works with existing code
✓ Single import for everything
✗ Larger bundle size


OPTION 2: Individual Modules (Recommended)
──────────────────────────────────────────
import { squadAPI, campaignAPI, facilityAPI } from './api';

await squadAPI.createSquadMember(...);
await campaignAPI.getCampaign(...);
await facilityAPI.createFacility(...);

✓ Clear which domain you're working with
✓ Better code organization
✓ Better tree-shaking


OPTION 3: Direct Module Import (Best Performance)
─────────────────────────────────────────────────
import { squadAPI } from './api/squad';
import { campaignAPI } from './api/campaigns';

await squadAPI.createSquadMember(...);
await campaignAPI.getCampaign(...);

✓ Smallest bundle size
✓ Fastest load time
✓ Most explicit
✗ Multiple import statements


EXAMPLE: Using in a Component
─────────────────────────────
import { squadAPI, campaignAPI } from './api';

function MyComponent() {
  const loadData = async () => {
    const campaign = await campaignAPI.getCampaign(123);
    const squad = await squadAPI.getSquad(campaign.id);
    
    // Do stuff with data
  };
  
  return <div>...</div>;
}


EXAMPLE: Organizing by Feature
──────────────────────────────
// In your squad management file
import { squadAPI } from './api';

export const recruitSoldier = async (campaignId, name, rank) => {
  return squadAPI.createSquadMember(campaignId, name, rank);
};

// In your campaign file  
import { campaignAPI } from './api';

export const startNewGame = async (userId, name) => {
  return campaignAPI.createCampaign(userId, name);
};


ADVANCED: Custom API Instances
──────────────────────────────
import { SquadAPI } from './api/squad';
import { APIClient } from './api/client';

// Custom client with different base URL
const devClient = new APIClient('http://localhost:8080');
const devSquadAPI = new SquadAPI(devClient);

// Now you have a dev version!
await devSquadAPI.getSquad(123);


MODULE STRUCTURE
───────────────
api/
├── client.js       → Base APIClient class
├── users.js        → User management (auth, profile, preferences)
├── campaigns.js    → Campaign CRUD and game state
├── squad.js        → Squad members, classes, skills
├── facilities.js   → Base building and upgrades
├── research.js     → Tech tree and research
├── missions.js     → Mission lifecycle and stats
├── events.js       → Event logging and timeline
└── index.js        → This file (combines everything)


WHY THIS STRUCTURE?
──────────────────
✓ Separation of Concerns    → Each file = one domain
✓ Easy Navigation            → Know exactly where code lives
✓ Testability               → Mock individual modules
✓ Scalability               → Add modules without touching others
✓ Team Collaboration        → Avoid merge conflicts
✓ Bundle Optimization       → Tree-shaking removes unused code
✓ Mental Model              → Matches your game architecture

*/